import { Box, Container } from '@mui/material';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import { Link, useParams } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import gsap from 'gsap';
import { useGSAP } from '@gsap/react';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Footer } from '../../components/footers/Footer';
import { HomeHeader } from '../../components/headers/HomeHeader';
import { PageMain } from '../../components/seo/PageMain';
import { PageSeoHelmet } from '../../components/seo/PageSeoHelmet';
import { ArticlePageHeader } from './components/ArticlePageHeader';
import { ArticleSections } from './components/ArticleSections';
import { ArticleToc } from './components/ArticleToc';
import {
  DEFAULT_EBOOK_SLUG,
  buildEbookChapterPage,
} from './repository';
import { KC_COLORS } from './theme';
import { NotFound } from '../notFound/NotFound';
import './knowledgeCenter.css';

gsap.registerPlugin(useGSAP, ScrollTrigger);

function ChapterPublicationMeta({ publication, subtitle }) {
  if (!publication && !subtitle) return null;

  const items = [
    publication?.series,
    publication?.category,
    publication?.learningPath || null,
  ].filter(Boolean);

  return (
    <div
      className="kc-guide-publication mt-5 md:mt-6 rounded-2xl border px-3.5 py-3.5 sm:px-4 sm:py-4 md:px-5 md:py-5 text-left"
      style={{
        borderColor: KC_COLORS.border,
        background:
          'linear-gradient(135deg, rgba(0,155,170,0.06) 0%, rgba(10,29,58,0.04) 100%)',
      }}
    >
      {subtitle ? (
        <p
          className="m-0 kc-type-body font-semibold break-words text-[15px] leading-6 sm:text-[16px] sm:leading-6"
          style={{ color: KC_COLORS.primaryNavy }}
        >
          {subtitle}
        </p>
      ) : null}
      {publication?.chapterPositioning &&
      publication.chapterPositioning !== subtitle ? (
        <p
          className={`m-0 kc-type-body break-words ${subtitle ? 'mt-2' : ''}`}
          style={{ color: KC_COLORS.gray600, fontSize: '14px', lineHeight: '22px' }}
        >
          {publication.chapterPositioning}
        </p>
      ) : null}
      {items.length ? (
        <div
          className={`flex flex-wrap gap-2 ${
            subtitle || publication?.chapterPositioning ? 'mt-3' : ''
          }`}
        >
          {items.map((item) => (
            <span
              key={item}
              className="inline-flex max-w-full items-center rounded-full border bg-white px-2.5 py-1 sm:px-3 sm:py-1.5 kc-type-caption font-medium break-words"
              style={{ borderColor: KC_COLORS.border, color: KC_COLORS.navy700 }}
            >
              {item}
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function ChapterNav({ prev, next, bookPath }) {
  return (
    <div className="mt-10 md:mt-12 grid grid-cols-1 md:grid-cols-2 gap-3.5">
      {prev ? (
        <Link
          to={prev.path}
          className="group flex items-start gap-3 rounded-2xl border bg-white px-4 py-4 no-underline transition-shadow hover:shadow-[0_8px_24px_rgba(10,29,58,0.08)]"
          style={{ borderColor: KC_COLORS.border }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 20, color: KC_COLORS.teal, mt: '2px' }} />
          <div className="min-w-0">
            <div className="kc-type-caption" style={{ color: KC_COLORS.gray600 }}>
              Previous chapter
            </div>
            <div
              className="font-semibold break-words mt-0.5"
              style={{ color: KC_COLORS.primaryNavy, fontSize: '15px', lineHeight: '22px' }}
            >
              {prev.title}
            </div>
          </div>
        </Link>
      ) : (
        <Link
          to={bookPath}
          className="group flex items-start gap-3 rounded-2xl border bg-white px-4 py-4 no-underline"
          style={{ borderColor: KC_COLORS.border }}
        >
          <ArrowBackRoundedIcon sx={{ fontSize: 20, color: KC_COLORS.teal, mt: '2px' }} />
          <div>
            <div className="kc-type-caption" style={{ color: KC_COLORS.gray600 }}>
              Back to ebook
            </div>
            <div
              className="font-semibold mt-0.5"
              style={{ color: KC_COLORS.primaryNavy, fontSize: '15px' }}
            >
              Table of contents
            </div>
          </div>
        </Link>
      )}

      {next ? (
        <Link
          to={next.path}
          className="group flex items-start justify-between gap-3 rounded-2xl border bg-white px-4 py-4 no-underline transition-shadow hover:shadow-[0_8px_24px_rgba(10,29,58,0.08)] md:text-right"
          style={{ borderColor: KC_COLORS.border }}
        >
          <div className="min-w-0 md:ml-auto">
            <div className="kc-type-caption" style={{ color: KC_COLORS.gray600 }}>
              Next chapter
            </div>
            <div
              className="font-semibold break-words mt-0.5"
              style={{ color: KC_COLORS.primaryNavy, fontSize: '15px', lineHeight: '22px' }}
            >
              {next.title}
            </div>
          </div>
          <ArrowForwardRoundedIcon
            sx={{ fontSize: 20, color: KC_COLORS.teal, mt: '2px', flexShrink: 0 }}
          />
        </Link>
      ) : (
        <Link
          to={bookPath}
          className="group flex items-start justify-between gap-3 rounded-2xl border bg-white px-4 py-4 no-underline md:text-right"
          style={{ borderColor: KC_COLORS.border }}
        >
          <div className="md:ml-auto">
            <div className="kc-type-caption" style={{ color: KC_COLORS.gray600 }}>
              End of ebook
            </div>
            <div
              className="font-semibold mt-0.5"
              style={{ color: KC_COLORS.primaryNavy, fontSize: '15px' }}
            >
              Return to table of contents
            </div>
          </div>
          <ArrowForwardRoundedIcon
            sx={{ fontSize: 20, color: KC_COLORS.teal, mt: '2px', flexShrink: 0 }}
          />
        </Link>
      )}
    </div>
  );
}

export function KnowledgeCenterEbookChapter() {
  const params = useParams();
  const bookSlug = params.ebookSlug || DEFAULT_EBOOK_SLUG;
  const chapterSlug = params.chapterSlug;
  const pageScope = useRef(null);
  const [page, setPage] = useState(null);
  const [status, setStatus] = useState('loading');

  useEffect(() => {
    let cancelled = false;
    setStatus('loading');
    setPage(null);

    buildEbookChapterPage(bookSlug, chapterSlug).then((nextPage) => {
      if (cancelled) return;
      if (!nextPage) {
        setStatus('missing');
        return;
      }
      setPage(nextPage);
      setStatus('ready');
      window.scrollTo({ top: 0, behavior: 'auto' });
    });

    return () => {
      cancelled = true;
    };
  }, [bookSlug, chapterSlug]);

  const scrollToSection = useCallback((sectionId) => {
    const el = document.getElementById(sectionId);
    if (!el) return;
    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  useGSAP(
    () => {
      if (!page) return undefined;

      const intro = gsap.timeline({ defaults: { ease: 'power2.out' } });
      intro
        .fromTo('.kc-article-breadcrumb', { y: 14, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 })
        .fromTo('.kc-article-title', { y: 26, opacity: 0 }, { y: 0, opacity: 1, duration: 0.55 }, '-=0.15')
        .fromTo('.kc-article-summary', { y: 16, opacity: 0 }, { y: 0, opacity: 1, duration: 0.45 }, '-=0.25')
        .fromTo('.kc-article-meta', { y: 12, opacity: 0 }, { y: 0, opacity: 1, duration: 0.4 }, '-=0.2');

      ScrollTrigger.create({
        trigger: '.kc-article-body',
        start: 'top 85%',
        once: true,
        onEnter: () => {
          gsap.fromTo(
            '.kc-article-section',
            { y: 22, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.45, stagger: 0.06, ease: 'power2.out' }
          );
          gsap.fromTo(
            '.kc-article-toc',
            { y: 20, opacity: 0 },
            { y: 0, opacity: 1, duration: 0.5, ease: 'power2.out', delay: 0.1 }
          );
        },
      });

      return undefined;
    },
    { scope: pageScope, dependencies: [bookSlug, chapterSlug, page] }
  );

  if (status === 'missing') return <NotFound />;

  if (status === 'loading' || !page) {
    return (
      <div className="kc-ds w-full overflow-x-hidden bg-white min-h-screen">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain className="knowledge-center-article-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <div className="pt-28 md:pt-32 pb-16">
                <p className="kc-type-body" style={{ color: KC_COLORS.gray600 }}>
                  Loading chapter…
                </p>
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    );
  }

  const bodySections = page.sections || [];

  return (
    <>
      <PageSeoHelmet {...page.seo} />
      <div className="kc-ds w-full overflow-x-hidden bg-white">
        <HomeHeader headerColor="#0D131E" textColor="#ffffff" />
        <PageMain ref={pageScope} className="knowledge-center-article-page">
          <Box className="w-full bg-white pb-12 md:pb-16">
            <Container maxWidth="lg">
              <ArticlePageHeader article={page} />
              <ChapterPublicationMeta
                publication={page.publication}
                subtitle={page.guideSubtitle}
              />

              <hr
                className="mt-8 md:mt-10 mb-8 md:mb-10 border-0 border-t"
                style={{ borderColor: KC_COLORS.border }}
              />

              <div className="kc-article-body text-left">
                {bodySections.length > 0 ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_280px] gap-8 lg:gap-10 items-start">
                    <div className="min-w-0 text-left">
                      <ArticleSections sections={bodySections} openFirstOnMobile />
                    </div>
                    <ArticleToc sections={bodySections} onNavigate={scrollToSection} />
                  </div>
                ) : null}

                <ChapterNav prev={page.prev} next={page.next} bookPath={page.bookPath} />

                <div className="mt-6">
                  <Link
                    to={page.bookPath}
                    className="font-semibold no-underline"
                    style={{ color: KC_COLORS.teal }}
                  >
                    View full table of contents →
                  </Link>
                </div>
              </div>
            </Container>
          </Box>
        </PageMain>
        <Footer />
      </div>
    </>
  );
}

export function KnowledgeCenterEbookChapterPage() {
  return <KnowledgeCenterEbookChapter />;
}
